import { useState } from 'react';

export default function TenantsLeasesFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "Do I have to tell my tenants I'm selling the property?",
      answer: "While most states don't legally require you to notify tenants before listing, it's highly recommended. Transparency builds trust and cooperation. You must notify tenants after accepting an offer and before closing, typically 30-60 days depending on state law. Proactive communication prevents problems and maintains good tenant relationships during the sale process."
    },
    {
      question: "Can tenants refuse to allow property showings?",
      answer: "No, tenants cannot completely refuse showings if your lease includes a showing clause and you provide proper notice (typically 24-48 hours). However, you must respect reasonable scheduling requests and cannot show the property excessively. If tenants are uncooperative, document all attempts and consider offering incentives for cooperation. Forced showings can damage tenant relationships and property presentation."
    },
    {
      question: "What happens to security deposits when I sell?",
      answer: "Security deposits must be transferred to the new owner with complete accounting. You cannot keep deposits when selling. The buyer assumes responsibility for returning deposits to tenants at lease end. Both parties should sign a security deposit transfer agreement at closing, and tenants must be notified in writing of the transfer and new owner's contact information within the timeframe required by state law (typically 5-30 days)."
    },
    {
      question: "Can I evict tenants to sell the property vacant?",
      answer: "You cannot evict tenants simply because you want to sell. Eviction requires legal grounds such as non-payment, lease violations, or lease expiration. In most states, you cannot force tenants out before their lease ends just to facilitate a sale. Cash-for-keys buyouts are a better alternative, offering tenants $1,000-$5,000 to voluntarily vacate. This is faster, cheaper, and avoids legal complications."
    },
    {
      question: "Do leases transfer automatically to the new owner?",
      answer: "Yes, existing leases automatically transfer to the new owner by operation of law when the property deed is transferred. The new owner must honor all lease terms, including rent amounts, lease duration, and special provisions. Tenants don't need to sign new leases or consent to the transfer. This is called 'privity of estate' and protects tenant rights during ownership changes."
    },
    {
      question: "What is an estoppel certificate and why do buyers require them?",
      answer: "An estoppel certificate is a document signed by tenants confirming their lease terms, rent amount, security deposit, and tenancy status. Buyers require these to verify that seller representations match tenant reality. Without estoppels, tenants could later claim different terms than what the seller disclosed. Most buyers require estoppels from all tenants before closing, and missing certificates can delay or kill deals."
    },
    {
      question: "How do month-to-month tenancies affect property value?",
      answer: "Month-to-month tenancies reduce property value by 2-10% depending on the percentage of units affected. While they offer flexibility, they create income uncertainty since tenants can leave with 30 days notice. If more than 20% of units are month-to-month, expect buyer concerns and potential price reductions. Convert month-to-month tenants to fixed-term leases 3-6 months before listing to maximize value."
    },
    {
      question: "Should I renew leases before selling or let them expire?",
      answer: "Renew leases that will expire during your anticipated sale period (listing through closing plus 90 days). Offer 12-month renewals at market rates. Properties with 6-12 months remaining on most leases are most attractive to buyers. Avoid renewing leases for 2-3 years right before selling, as this locks buyers into long-term commitments they may not want."
    },
    {
      question: "What if I have problem tenants when selling?",
      answer: "Address problem tenants before listing. Options include: cash-for-keys buyouts ($2,000-$5,000), payment plans for arrears, lease amendments to resolve violations, or eviction as a last resort. Problem tenants can reduce property value by 10-25% per unit. Full disclosure to buyers is essential. Some buyers will accept problem tenants with price adjustments, while others will walk away."
    },
    {
      question: "How much notice do I need to give tenants for showings?",
      answer: "Notice requirements vary by state, typically 24-48 hours. Some states require as little as 12 hours (Florida) while others require 48 hours (Washington). Always provide written notice via multiple methods (door notice, email, text). Schedule showings at reasonable times and limit frequency to avoid harassment claims. Offering incentives for showing cooperation increases tenant cooperation significantly."
    }
  ];

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-question-answer-line"></i>
            <span>Common Questions</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-slate-600">
            Get answers to the most common questions about managing tenants and leases during apartment building sales.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors duration-200"
              >
                <span className="font-semibold text-slate-900 pr-8">{faq.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-2xl text-teal-600 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-3xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-3">Still Have Questions?</h3>
          <p className="text-slate-700 mb-6 max-w-2xl mx-auto">
            Every property and situation is unique. Get personalized guidance on managing your specific tenant and lease challenges during the sale process.
          </p>
          <a
            href="tel:5551234567"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-phone-line text-xl"></i>
            <span>Call Us: (555) 123-4567</span>
          </a>
        </div>
      </div>
    </section>
  );
}
